import React from 'react';
import { COVERAGE_FIELDS, Dataset } from 'app/data/datasets';
import { DataLink, Panel } from './ui';

/**
 * The dataset's anatomy — the artifact the whole page is built around.
 *
 * Modelled on turbopuffer's production-limits table: a dense, monospace grid
 * that publishes the real state of every field, including the unflattering
 * ones. Its credibility comes from what it declines to hide.
 *
 * HONESTY RULE: every field in COVERAGE_FIELDS gets a row, always. A field the
 * registry leaves `undefined` renders as an explicit, muted, dashed-rule
 * "Not yet documented" — never hidden, never guessed, never filled with a
 * plausible-looking placeholder. Do not "improve" this by inventing values.
 */

interface RenderedValue {
  documented: boolean;
  node: React.ReactNode;
}

const NOT_DOCUMENTED: RenderedValue = {
  documented: false,
  node: 'Not yet documented',
};

function renderValue(dataset: Dataset, key: keyof Dataset): RenderedValue {
  const value = dataset[key];
  if (value === undefined || value === null) return NOT_DOCUMENTED;

  switch (key) {
    case 'historyStart':
      return {
        documented: true,
        node: <time dateTime={String(value)}>{String(value)}</time>,
      };

    case 'entities':
      // The registry documents these as approximate and growing, so "+" is
      // carrying meaning here, not decoration.
      return {
        documented: true,
        node: (
          <>
            {(value as number).toLocaleString('en-US')}+{' '}
            <span className="text-gray-400">approximate</span>
          </>
        ),
      };

    case 'rows':
      return {
        documented: true,
        node: (value as number).toLocaleString('en-US'),
      };

    case 'identifiers': {
      const ids = value as string[];
      if (ids.length === 0) return NOT_DOCUMENTED;
      return { documented: true, node: ids.join('  ·  ') };
    }

    case 'schema': {
      const schema = value as NonNullable<Dataset['schema']>;
      if (schema.length === 0) return NOT_DOCUMENTED;
      return {
        documented: true,
        node: `${schema.length} column${
          schema.length === 1 ? '' : 's'
        } — listed below`,
      };
    }

    default:
      return { documented: true, node: String(value) };
  }
}

export const SpecificationsTable: React.FC<{ dataset: Dataset }> = ({
  dataset,
}) => {
  const rows = COVERAGE_FIELDS.map(field => ({
    ...field,
    ...renderValue(dataset, field.key),
  }));
  const documented = rows.filter(r => r.documented).length;

  return (
    <>
      <Panel
        id="specifications-heading"
        label="Specifications"
        aside={`${documented} / ${rows.length} documented`}
      >
        {/* Wide content scrolls in its own container; the page body never does. */}
        <div className="overflow-x-auto">
          <table className="w-full min-w-[36rem] border-collapse font-mono text-[13px]">
            <caption className="sr-only">
              Coverage and delivery specifications for {dataset.name}. Fields
              that have not been published are marked "Not yet documented".
            </caption>
            <thead>
              <tr className="border-b border-gray-700">
                <th
                  scope="col"
                  className="w-[30%] py-1 pr-4 text-left text-[11px] uppercase tracking-[0.12em] font-medium text-gray-400"
                >
                  Field
                </th>
                <th
                  scope="col"
                  className="w-[38%] py-1 pr-4 text-left text-[11px] uppercase tracking-[0.12em] font-medium text-gray-400"
                >
                  Value
                </th>
                <th
                  scope="col"
                  className="py-1 text-left text-[11px] uppercase tracking-[0.12em] font-medium text-gray-400"
                >
                  Why it matters
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr
                  key={String(row.key)}
                  className={`border-b last:border-b-0 ${
                    row.documented
                      ? 'border-gray-800'
                      : 'border-dashed border-gray-800/80'
                  }`}
                >
                  <th
                    scope="row"
                    className="py-1 pr-4 text-left align-top font-normal text-gray-300 whitespace-nowrap"
                  >
                    {row.label}
                  </th>
                  <td
                    className={`py-1 pr-4 align-top tabular-nums ${
                      row.documented
                        ? 'text-white'
                        : // gray-400 (7.45:1), not gray-500 (3.91:1) — a gap
                          // marker has to stay readable. Italics carry the
                          // "quieter" reading instead of failing contrast.
                          'italic text-gray-400'
                    }`}
                  >
                    {row.node}
                    {!row.documented && (
                      <span className="sr-only">
                        {' '}
                        — this specification has not been published yet
                      </span>
                    )}
                  </td>
                  <td className="py-1 align-top text-gray-400">{row.why}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="mt-3 border-t border-gray-800 pt-3 text-[13px] leading-relaxed text-gray-400">
          Rows reading{' '}
          <span className="font-mono italic text-gray-400">
            Not yet documented
          </span>{' '}
          are unpublished measurements, not gaps in this page. A wrong coverage
          figure is worse than a missing one, so nothing here is estimated. Ask
          and we will measure it.
        </p>
      </Panel>

      {/*
        Measured as too wide to enumerate: the registry carries the real column
        count but no schema, because listing hundreds or thousands of columns
        would be noise rather than information. State the width and hand off to
        the documentation instead of silently showing nothing.
      */}
      {!dataset.schema && typeof dataset.columnCount === 'number' && (
        <Panel
          id="schema-heading"
          label="Schema"
          aside={`${dataset.columnCount.toLocaleString('en-US')} columns`}
        >
          <p className="max-w-3xl text-[13px] leading-relaxed text-gray-300">
            This dataset returns{' '}
            <span className="font-mono tabular-nums text-white">
              {dataset.columnCount.toLocaleString('en-US')}
            </span>{' '}
            columns — measured from a live pull, and too wide to enumerate
            usefully here. The column list and field definitions live in{' '}
            <DataLink href={dataset.docsUrl}>the documentation</DataLink>.
          </p>
        </Panel>
      )}

      {dataset.schema && dataset.schema.length > 0 && (
        <Panel
          id="schema-heading"
          label="Schema"
          aside={`${dataset.schema.length} columns`}
        >
          <div className="overflow-x-auto">
            <table className="w-full min-w-[32rem] border-collapse font-mono text-[13px]">
              <thead>
                <tr className="border-b border-gray-700">
                  <th
                    scope="col"
                    className="py-1 pr-4 text-left text-[11px] uppercase tracking-[0.12em] font-medium text-gray-400"
                  >
                    Column
                  </th>
                  <th
                    scope="col"
                    className="py-1 pr-4 text-left text-[11px] uppercase tracking-[0.12em] font-medium text-gray-400"
                  >
                    Type
                  </th>
                  <th
                    scope="col"
                    className="py-1 text-left text-[11px] uppercase tracking-[0.12em] font-medium text-gray-400"
                  >
                    Description
                  </th>
                </tr>
              </thead>
              <tbody>
                {dataset.schema.map(column => (
                  <tr
                    key={column.name}
                    className="border-b border-gray-800 last:border-b-0"
                  >
                    <td className="py-1 pr-4 align-top text-green-400">
                      {column.name}
                    </td>
                    <td className="py-1 pr-4 align-top text-gray-300">
                      {column.type}
                    </td>
                    <td className="py-1 align-top text-gray-400">
                      {column.description ?? (
                        <span className="italic">Not yet documented</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Panel>
      )}
    </>
  );
};

export default SpecificationsTable;
